/** @module SiteAdmin/BusinessObject */
define([
	"csi/html",
	"csi/reflection",
	"csi/resolver",
	"csi/widget/CheckBox",
	"csi/xml",
	"SiteAdmin/Locale",
	"src/c8-platform/type/node",
	"src/c8-platform/type/uom",
	"src/c8-platform/type/value",
], function(html, reflection, resolver, CheckBox, xml, Locale, {NODE_TYPE},
	{getUOMConversionString, getUOMValueString, isConversionUOMAttribute, isConversionUOMCustomAttribute, isValueUOMCustomAttribute}, {isRefValueType}) {

	/**
	 * Return attribute reflection class for Business Object
	 * @param {Node} node
	 * @return {Class}
	 */
	function getAttributeClass(node) {
		return reflection.getClass(getAttributeType(node));
	}

	/**
	 * Return node type where attributes are added by default for Business Object
	 * @param {Node} node
	 * @return {string}
	 */
	function getAttributeType(node) {
		return node.AttributeClassName;
	}

	/**
	 * Return Business Object attribute URL
	 * @param {String} businessObjectName
	 * @param {String} attributeId
	 * @return {string}
	 */
	function getBOCustomAttributeURL(businessObjectName, attributeId) {
		return `${reflection.URL}${NODE_TYPE.CUSTOM_ATTRIBUTE}/${businessObjectName}/${attributeId}`;
	}

	/**
	 * Return root reflection class for Business Object
	 * @param {Node} node
	 * @return {Class}
	 */
	function getRootClass(node) {
		return reflection.getClass(getRootType(node));
	}

	/**
	 * Return root node type represented by Business Object
	 * @param {Node} node
	 * @return {string}
	 */
	function getRootType(node) {
		return node.RootClassName;
	}

	/**
	 * Return true if attribute already has an aggregate store or if attribute is custom;
	 * do not allow customers to create an aggregate store for OOTB attributes whose AggregateInline
	 * action has not yet been converted to an aggregate store
	 * @param {Context} ctx
	 * @param {Action} action
	 * @return {boolean}
	 */
	function onAccessAggregateStore(ctx, action) {
		const node = ctx.getNode();
		const {ReflectionAttributeId, ReflectionAttributeOwner} = node;
		const targetClass = node[reflection.ATTR_TARGET_CLASS_NAME];
		const valueType = node[reflection.ATTR_VALUETYPE];
		const isCustom = (node.$Type === NODE_TYPE.CUSTOM_ATTRIBUTE);
		const ra = reflection.getTypeAttribute(ReflectionAttributeOwner, ReflectionAttributeId);

		// do not show Aggregate Store icon for UOMConversion attributes
		const isUOMConversionAttribute = this.onAccessIsUOMConversionAttribute(ctx, action);

		// do not allow creating or overriding the aggregate store for Image and Tag refs as they have custom editing behavior
		const invalidTargetClasses = new Set([reflection.TYPE_IMAGE, reflection.TYPE_TAG]);
		const invalidReflectionAttributeIds = new Set([reflection.ATTR_IMAGES, reflection.ATTR_TAGS]);

		// NOTE: currently prevent overriding a reverse aggregate store - it may be possible in future, but no time in 7.10
		return !isUOMConversionAttribute &&
			isRefValueType(valueType) &&
			!ra?.isReverseRelation &&
			!invalidTargetClasses.has(targetClass) &&
			!(targetClass === undefined && invalidReflectionAttributeIds.has(ReflectionAttributeId)) &&
			(isCustom || reflection.hasAggregateStoreForAttribute(ReflectionAttributeOwner, ReflectionAttributeId));
	}

	return {
		// Business Object related types and attributes
		ATTR_MODIFIED: "Modified",
		ATTR_ATTRIBUTE_FLAGS: "Attribute Flags",
		ATTR_TARGET_CLASS_NAME: "TargetClassName",
		ATTR_TARGET_CLASS_SUBTYPE_ATTRIBUTE_ID: "TargetClassSubtypeAttributeId",
		ATTR_REST_API_EXPOSED: "RestApiExposed",
		ATTR_DRAG_EDIT_DISABLED: "DragEditDisabled",
		ATTR_EXP_ONCLIENT_ENABLED: "ExpressionOnClientEnabled",

		getAttributeClass,
		getAttributeType,
		getBOCustomAttributeURL,
		getRootType,
		getRootClass,
		onAccessAggregateStore,

		onAccessActionCustomAttribute: function(/*Context*/ctx, /*Action*/action) {
			// disable adding attributes to "Home" BO
			return xml.nodeGetName(ctx.getParent()) != "Home";
		},

		/**
		 * Return true if node is a normal attribute and not a UOM attribute
		 * C8FB-292: Only allow delete if node has no "NO_DELETE" flag
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		onAccessActionMarkForDeletionCustomAttribute(ctx, action) {
			return !xml.nodeHasFlag(ctx.getNode(), xml.NODE_FLAG_NODELETE) && !this.onAccessIsUOMCustomAttribute(ctx, action);
		},

		/**
		 * Return true if node is UOM Value attribute and does not have the "NO_DELETE" flag
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		onAccessActionMarkForDeletionUOMCustomAttribute(ctx, action) {
			return !xml.nodeHasFlag(ctx.getNode(), xml.NODE_FLAG_NODELETE) && this.onAccessIsUOMValueCustomAttribute(ctx, action);
		},

		/**
		 * Return true if node is UOM Conversion attribute
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		onAccessIsUOMConversionAttribute(ctx, action) {
			const node = ctx.getNode();
			const uomValueNode = ctx.cache.getNode(getUOMValueString(node.$URL));
			return isConversionUOMAttribute(node, uomValueNode);
		},

		/**
		 * Return true if node is UOM Conversion custom attribute
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		onAccessIsUOMConversionCustomAttribute(ctx, action) {
			const node = ctx.getNode();
			const uomValueNode = ctx.cache.getNode(getUOMValueString(node.$URL));
			return isConversionUOMCustomAttribute(node, uomValueNode);
		},

		/**
		 * Return true if node is a UOM custom attribute (UOM Conversion or UOM Value)
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		onAccessIsUOMCustomAttribute(ctx, action) {
			return this.onAccessIsUOMValueCustomAttribute(ctx, action) || this.onAccessIsUOMConversionCustomAttribute(ctx, action);
		},

		/**
		 * Return true if node is UOM Value custom attribute
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		onAccessIsUOMValueCustomAttribute(ctx, action) {
			const node = ctx.getNode();
			const uomConversionNode = ctx.cache.getNode(getUOMConversionString(node.$URL));
			return isValueUOMCustomAttribute(node, uomConversionNode);
		},

		/**
		 * Return true if node is not UOM Conversion attribute
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		onAccessIsNotUOMConversionAttribute(ctx, action) {
			return !this.onAccessIsUOMConversionAttribute(ctx, action)
				&& !this.onAccessIsUOMConversionCustomAttribute(ctx, action);
		},

		/**
		 * Return true if node is not UOM Conversion custom attribute
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		onAccessIsNotUOMConversionCustomAttribute(ctx, action) {
			return !this.onAccessIsUOMConversionCustomAttribute(ctx, action);
		},

		/**
		 * Return true if node is not UOM custom attribute (not UOM Conversion or UOM Value)
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		onAccessIsNotUOMCustomAttribute(ctx, action) {
			return !this.onAccessIsUOMCustomAttribute(ctx, action);
		},

		/**
		 * Return true if node is not UOM Value custom attribute
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		onAccessIsNotUOMValueCustomAttribute(ctx, action) {
			return !this.onAccessIsUOMValueCustomAttribute(ctx, action);
		},

		onAccessActionRestApiBlockChanges: function(/*Context*/ctx, /*Action*/action) {
			// TA29830 Make RestApiExposed flag editable depending on Rest API Block Changes flag
			var site = resolver.resolveNode(reflection.URL_SITE, true);
			return !site.RestApiBlockChanges;
		},

		/**
		 * Determine render the action/tab or not
		 */
		onAccessCommentCategory: function(ctx, action) {
			return this.onAccessCommentCategoryTab(ctx.getNode(), ctx.cache);
		},

		onAccessCommentCategoryTab: function(node, cache) {
			const rc = getRootClass(node);
			const commentContainer = reflection.getClass("CommentContainer");
			const docAndCommentOwner = reflection.getClass("DocAndCommentOwner");

			return rc && (rc.isDerivedFrom(commentContainer) || rc.isDerivedFrom(docAndCommentOwner));
		},

		/**
		 * Return false if Business Object's reflection class is not abstract and belongs to more than one domain object
		 * @param {C8Node} node 
		 * @param {Cache} cache 
		 * @return {boolean}
		 */
		onAccessTabAttributeGroups(node, cache) {
			const rc = reflection.getClass(node.RootClassName);
			return rc.abstract || node.DomainClassName;
		},

		onAccessTabMasterClass: function(node, cache) {
			const rc = reflection.getClass(node.RootClassName);
			return rc.isDerivedFrom("_CS_Master");
		},

		onAccessTabNonMasterClass: function(node, cache) {
			const rc = reflection.getClass(node.RootClassName);
			return !rc.isDerivedFrom("_CS_Master");
		},

		onActionModifyConfiguration: function(ac) {
			// It's not necessary set 'Modified' flag to 'true' because the server sets it.
			// But we do it anyway to allow UI show the state immediately.
			ac.m_writer.changeNode(ac.m_urlNode).changeAttribute(this.ATTR_MODIFIED, xml.ATTR_BOOLEAN, true);
			ac.bRefreshNode = true;
		},

		/** the server will set the modified flag on the cell's node so make sure it's set on the row's node */
		onActionModifyConfigurationRow(ac) {
			ac.m_writer.changeNode(ac.refNodeId).changeAttribute(this.ATTR_MODIFIED, xml.ATTR_BOOLEAN, true);
			ac.bRefreshNode = true;
		},

		onDrawDisplayFormat: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode) {
			var nodeBO = ctx.getNode();
			var type = getRootType(nodeBO);

			var attr1 = Locale.getTypeAttribute(type, nodeBO.DisplayNameAttribute1);
			if (attr1) {
				var attr2 = Locale.getTypeAttribute(type, nodeBO.DisplayNameAttribute2) || "";
				html.text(refNode, attr1 + nodeBO.DisplayNameSeparator + attr2);
			}
		},

		onDrawFlagReadOnly: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode) {
			this._onDrawFlag(ctx, refNode, xml.ATTR_FLAG_NOEDIT);
		},

		_onDrawFlag: function(/*Context*/ctx, /*DOMNode*/refNode, /*Number*/nBit) {
			new CheckBox({
				disabled: true,
				value: xml.valueHasFlag(ctx.getNode()["Attribute Flags"], nBit)
			}).placeAt(refNode);
		},

		areImageLabelsPublished: function(/*BusinessObject*/node) {
			// image labels are published if they are in the reflection cache
			return !!reflection.getClass(node.RootClassName).imageLabelEnum;
		}
	};
});